import { lsGet, lsRemove, lsSet } from '@/utils/storage'

export type ScreeningColorProfile =
  | 'attention'
  | 'mood'
  | 'anxiety'
  | 'personality'
  | 'stress'
  | 'sleep'
  | 'pain'
  | 'habits'

export interface ScreeningColorProfileOption {
  value: ScreeningColorProfile
  label: string
  description: string
  hue: number
}

const COLOR_DEFAULT_STORAGE_KEY = 'color-profile-default'
const COLOR_OVERRIDE_STORAGE_KEY = 'color-profile-override'
const COLOR_ATTRIBUTE = 'data-color'
let defaultScreeningColorProfile: ScreeningColorProfile | null = null

export const SCREENING_COLOR_PROFILE_OPTIONS: ScreeningColorProfileOption[] = [
  { value: 'attention', label: 'Attention', description: 'ASRS, CFQ', hue: 240 },
  { value: 'mood', label: 'Mood', description: 'PHQ-9, MDQ, RSES', hue: 270 },
  { value: 'anxiety', label: 'Anxiety', description: 'GAD-7, LSAS, PCL-5, OCI-R', hue: 310 },
  { value: 'personality', label: 'Personality', description: 'Big Five, HEXACO, AQ', hue: 350 },
  { value: 'stress', label: 'Stress & Burnout', description: 'PSS, CBI, FAS', hue: 60 },
  { value: 'sleep', label: 'Sleep', description: 'BIS', hue: 220 },
  { value: 'pain', label: 'Pain & Body', description: 'BPI, FM, HIT-6, IBS', hue: 90 },
  { value: 'habits', label: 'Habits', description: 'IAT, SAS-SV, PGSI, EDE-Q, SCOFF', hue: 190 },
]

function isScreeningColorProfile(value: string): value is ScreeningColorProfile {
  return SCREENING_COLOR_PROFILE_OPTIONS.some((option) => option.value === value)
}

export function normalizeScreeningColorProfile(value?: string | null): ScreeningColorProfile | null {
  if (!value) {
    return null
  }

  return isScreeningColorProfile(value) ? value : null
}

function setDocumentColorAttribute(attribute: string, profile?: ScreeningColorProfile | null) {
  if (typeof document === 'undefined') {
    return
  }

  if (!profile) {
    document.documentElement.removeAttribute(attribute)
    return
  }

  document.documentElement.setAttribute(attribute, profile)
}

export function getScreeningColorProfileOverride(): ScreeningColorProfile | null {
  return normalizeScreeningColorProfile(lsGet(COLOR_OVERRIDE_STORAGE_KEY))
}

export function getDefaultScreeningColorProfile(): ScreeningColorProfile | null {
  return defaultScreeningColorProfile ?? normalizeScreeningColorProfile(lsGet(COLOR_DEFAULT_STORAGE_KEY))
}

export function getAppliedScreeningColorProfile(): ScreeningColorProfile | null {
  if (typeof document === 'undefined') {
    return null
  }

  return normalizeScreeningColorProfile(
    document.documentElement.getAttribute(COLOR_ATTRIBUTE),
  )
}

export function setScreeningColorProfile(profile?: ScreeningColorProfile | null) {
  setDocumentColorAttribute(COLOR_ATTRIBUTE, profile)
}

export function applyStoredScreeningColorProfile() {
  setScreeningColorProfile(getScreeningColorProfileOverride() ?? getDefaultScreeningColorProfile())
}

export function applyScreeningColorProfileOverride() {
  applyStoredScreeningColorProfile()
}

export function setDefaultScreeningColorProfile(profile?: ScreeningColorProfile | null) {
  defaultScreeningColorProfile = profile ?? null

  if (profile) {
    lsSet(COLOR_DEFAULT_STORAGE_KEY, profile)
  } else {
    lsRemove(COLOR_DEFAULT_STORAGE_KEY)
  }

  if (!getScreeningColorProfileOverride()) {
    setScreeningColorProfile(defaultScreeningColorProfile)
  }
}

export function setScreeningColorProfileOverride(profile?: ScreeningColorProfile | null) {
  if (profile) {
    lsSet(COLOR_OVERRIDE_STORAGE_KEY, profile)
    setScreeningColorProfile(profile)
    return
  }

  lsRemove(COLOR_OVERRIDE_STORAGE_KEY)
  setScreeningColorProfile(getDefaultScreeningColorProfile())
}
